import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';

function ActionLogExplorerPageView({
  rows,
  filters,
  total,
  page,
  isLoading,
  error,
  success,
  canGoPrevious,
  canGoNext,
  updateFilter,
  applyFilters,
  resetFilters,
  refresh,
  nextPage,
  previousPage,
}) {
  const { t } = useTranslation();

  const handleSubmit = (event) => {
    event.preventDefault();
    applyFilters();
  };

  return (
    <>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={t('actionLogExplorer.title')} subtitle={t('actionLogExplorer.subtitle')} />

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">{t('actionLogExplorer.filters')}</h5>
              <p className="text-muted mb-0">{t('actionLogExplorer.filtersSubtitle')}</p>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={refresh} disabled={isLoading}>
              <i className="ti ti-refresh me-1" />
              {t('actionLogExplorer.refresh')}
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-3 align-items-end">
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-actor-uid">{t('actionLogExplorer.actorUid')}</label>
                <input
                  id="action-log-actor-uid"
                  className="form-control"
                  value={filters.actorUid}
                  onChange={(event) => updateFilter('actorUid', event.target.value)}
                />
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-profile-uid">{t('actionLogExplorer.profileUid')}</label>
                <input
                  id="action-log-profile-uid"
                  className="form-control"
                  value={filters.profileUid}
                  onChange={(event) => updateFilter('profileUid', event.target.value)}
                />
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-action">{t('actionLogExplorer.action')}</label>
                <input
                  id="action-log-action"
                  className="form-control"
                  value={filters.action}
                  onChange={(event) => updateFilter('action', event.target.value)}
                />
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-ip">{t('actionLogExplorer.ip')}</label>
                <input
                  id="action-log-ip"
                  className="form-control"
                  value={filters.ip}
                  onChange={(event) => updateFilter('ip', event.target.value)}
                />
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-from">{t('actionLogExplorer.from')}</label>
                <input
                  id="action-log-from"
                  type="datetime-local"
                  className="form-control"
                  value={filters.from}
                  onChange={(event) => updateFilter('from', event.target.value)}
                />
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-to">{t('actionLogExplorer.to')}</label>
                <input
                  id="action-log-to"
                  type="datetime-local"
                  className="form-control"
                  value={filters.to}
                  onChange={(event) => updateFilter('to', event.target.value)}
                />
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-support-grant-id">{t('actionLogExplorer.supportGrantId')}</label>
                <input
                  id="action-log-support-grant-id"
                  className="form-control"
                  inputMode="numeric"
                  value={filters.supportGrantId}
                  onChange={(event) => updateFilter('supportGrantId', event.target.value)}
                />
              </div>
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label className="form-label" htmlFor="action-log-request-id">{t('actionLogExplorer.requestId')}</label>
                <input
                  id="action-log-request-id"
                  className="form-control"
                  value={filters.requestId}
                  onChange={(event) => updateFilter('requestId', event.target.value)}
                />
              </div>
              <div className="col-xl-2 col-lg-3 col-md-4">
                <label className="form-label" htmlFor="action-log-order">{t('actionLogExplorer.order')}</label>
                <select
                  id="action-log-order"
                  className="form-select"
                  value={filters.order}
                  onChange={(event) => updateFilter('order', event.target.value)}
                >
                  <option value="desc">desc</option>
                  <option value="asc">asc</option>
                </select>
              </div>
              <div className="col-xl-2 col-lg-3 col-md-4">
                <label className="form-label" htmlFor="action-log-limit">{t('actionLogExplorer.limit')}</label>
                <select
                  id="action-log-limit"
                  className="form-select"
                  value={filters.limit}
                  onChange={(event) => updateFilter('limit', event.target.value)}
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className="col-xl-4 col-lg-6 col-md-8 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  <i className="ti ti-filter me-1" />
                  {t('actionLogExplorer.applyFilters')}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={resetFilters} disabled={isLoading}>
                  {t('common.reset')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">{t('actionLogExplorer.records')}</h5>
              <p className="text-muted mb-0">{t('actionLogExplorer.recordsSubtitle')}</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-light text-dark">{total}</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={previousPage} disabled={isLoading || !canGoPrevious}>
                <i className="ti ti-chevron-left" />
              </button>
              <span className="small text-muted">{page + 1}</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={nextPage} disabled={isLoading || !canGoNext}>
                <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table search-table align-middle mb-0">
              <thead className="header-item">
                <tr>
                  <th style={{ minWidth: '170px' }}>{t('actionLogExplorer.created')}</th>
                  <th style={{ minWidth: '190px' }}>{t('actionLogExplorer.action')}</th>
                  <th style={{ minWidth: '190px' }}>{t('actionLogExplorer.actor')}</th>
                  <th style={{ minWidth: '190px' }}>{t('actionLogExplorer.profile')}</th>
                  <th style={{ minWidth: '130px' }}>{t('actionLogExplorer.ip')}</th>
                  <th style={{ minWidth: '280px' }}>{t('actionLogExplorer.detailsSummary')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-muted">{t('common.loading')}</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-muted">{t('actionLogExplorer.noRecords')}</td>
                  </tr>
                ) : rows.map((row) => (
                  <tr key={row.key}>
                    <td className="text-nowrap">{row.createdLabel}</td>
                    <td>{row.action}</td>
                    <td>
                      <div>{row.actorLabel}</div>
                      {row.actorUid ? <div className="font-monospace small text-muted text-break">{row.actorUid}</div> : null}
                    </td>
                    <td>
                      <div>{row.profileLabel}</div>
                      {row.profileUid ? <div className="font-monospace small text-muted text-break">{row.profileUid}</div> : null}
                    </td>
                    <td className="font-monospace small text-break">{row.ip}</td>
                    <td className="text-break">{row.detailsSummary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default ActionLogExplorerPageView;
